import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, Plus } from 'lucide-react';
import { apiGet } from './appApi';
import { formatDateShort } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  draft: 'bg-gray-100 text-gray-700',
};

export const AppInvoices = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    apiGet('/api/admin/invoices')
      .then(setList)
      .catch((err) => logger.error('Load invoices:', err))
      .finally(() => setLoading(false));
  }, []);

  const q = query.toLowerCase();
  const filtered = list.filter(i => {
    if (filter !== 'all' && i.status !== filter) return false;
    return !q ||
      (i.client_name || '').toLowerCase().includes(q) ||
      (i.invoice_number || '').toLowerCase().includes(q);
  });

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'paid', label: 'Paid' },
    { key: 'overdue', label: 'Overdue' },
  ];

  return (
    <div>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div
          className="px-4 pb-3"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-gray-900">Invoices</h1>
            <button
              onClick={() => navigate('/admin/invoices/new')}
              className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center active:scale-95"
              aria-label="New invoice"
              data-testid="new-invoice-btn"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Search invoices"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-100 rounded-xl border-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                  filter === t.key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 py-3 space-y-2">
        {loading ? (
          [0, 1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {query || filter !== 'all' ? 'No matches' : 'No invoices yet'}
            </p>
          </div>
        ) : (
          filtered.map(inv => (
            <button
              key={inv.id}
              onClick={() => navigate(`/admin/invoices/${inv.id}`)}
              className="w-full bg-white border border-gray-200 rounded-xl p-3 text-left active:bg-gray-50"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-semibold text-gray-900 truncate">{inv.client_name}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${STATUS_COLORS[inv.status] || 'bg-gray-100 text-gray-600'}`}>
                  {inv.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 font-mono">{inv.invoice_number}</p>
                <p className="text-sm font-bold text-gray-900">
                  {inv.currency}{(inv.total || 0).toLocaleString()}
                </p>
              </div>
              {inv.invoice_date && (
                <p className="text-xs text-gray-400 mt-1">{formatDateShort(inv.invoice_date)}</p>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
};
